/**
 * Registro de un cliente sin factura (alta y edición).
 *
 * La persona nace en el sistema al facturarla; este diálogo la da de alta
 * antes (prospecto, cliente que vuelve otro día) en la tabla
 * `clienteregistro`. Desde ese momento sale en la búsqueda global y en el
 * Expediente del Cliente, y la venta (crédito, contado, solicitud,
 * cotización...) se precarga al digitar su documento.
 *
 * Si el documento ya tiene facturas o préstamos el backend lo rechaza: esa
 * persona ya tiene expediente y sus datos se editan desde la venta. El
 * diálogo lo detecta al salir del documento y ofrece abrir el expediente.
 *
 *   <RegistroClienteDialog open cedula="" onClose={...} onGuardado={(ced) => ...} />
 *
 * Con `cedula` abre en modo edición (documento fijo).
 */
import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, DocumentoField, fpInputSx } from "components/financepro";
import {
  CamposExtraForm,
  completarVacios,
  useFormularioExtra,
  usePersonaExtra,
} from "components/camposextra";
import { useApi } from "lib/api";
import { documentoValido, tipoDocumento } from "lib/documento";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const FORM_VACIO = {
  cedula: "",
  nombres: "",
  apellidos: "",
  edad: "",
  apodo: "",
  telefono: "",
  celular: "",
  email: "",
  direccion: "",
  provincia: "",
  comentario: "",
};

// Fila del backend (provincia en `urb`, como en solicit) → formulario.
const desdeRegistro = (c) => ({
  cedula: c.cedula || "",
  nombres: c.nombres || "",
  apellidos: c.apellidos || "",
  edad: c.edad != null ? String(c.edad) : "",
  apodo: c.apodo || "",
  telefono: c.telefono || "",
  celular: c.celular || "",
  email: c.email || "",
  direccion: c.direccion || "",
  provincia: c.urb || "",
  comentario: c.comentario || "",
});

export default function RegistroClienteDialog({
  open,
  cedula = "",
  onClose,
  onGuardado,
  onAbrirExpediente,
}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const cargarPersonaExtra = usePersonaExtra();
  // Solo las secciones que describen a la persona: las de las condiciones
  // del préstamo no aplican a quien todavía no compra.
  const secciones = useFormularioExtra().filter((s) => s.codeudor || s.tabVenta === "generales");

  const edicion = Boolean(cedula);
  const [form, setForm] = useState(FORM_VACIO);
  const [tipodoc, setTipodoc] = useState("cedula");
  const [datosExtra, setDatosExtra] = useState({});
  const [provincias, setProvincias] = useState([]);
  const [existente, setExistente] = useState(false);
  const [tieneVenta, setTieneVenta] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const aplicar = (data) => {
    if (Array.isArray(data.provincias)) setProvincias(data.provincias);
    setTieneVenta(Boolean(data.tieneventa));
    setExistente(Boolean(data.cliente));
    if (data.cliente) {
      setForm(desdeRegistro(data.cliente));
      setTipodoc(tipoDocumento(data.cliente.cedula));
      setDatosExtra(data.datosextra || {});
    }
  };

  useEffect(() => {
    if (!open) return;
    setForm({ ...FORM_VACIO, cedula });
    setTipodoc(cedula ? tipoDocumento(cedula) : "cedula");
    setDatosExtra({});
    setExistente(false);
    setTieneVenta(false);
    setCargando(true);
    post("/api/clienteregistro_obtener", { cedula })
      .then(({ ok, data }) => {
        if (ok) aplicar(data);
        else showAlert({ message: data.Error || "No se pudo cargar el cliente", color: "error" });
      })
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cedula]);

  // Alta: al salir del documento se mira si ya está registrado (pasa a
  // edición) o si ya tiene ventas (no se registra), y se precargan los
  // campos adicionales que la persona ya tenga como co-deudor.
  const revisarDocumento = async () => {
    if (edicion || !documentoValido(form.cedula, tipodoc)) return;
    const { ok, data } = await post("/api/clienteregistro_obtener", { cedula: form.cedula });
    if (!ok) return;
    aplicar(data);
    if (!data.cliente) {
      const ficha = await cargarPersonaExtra(form.cedula, { cliente: true });
      setDatosExtra((p) => completarVacios(p, ficha));
    }
  };

  const guardar = async () => {
    if (!documentoValido(form.cedula, tipodoc)) {
      showAlert({ message: "El documento no es válido", color: "warning" });
      return;
    }
    if (!form.nombres.trim()) {
      showAlert({ message: "Indique el nombre del cliente", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/clienteregistro_guardar", {
        cliente: form,
        datosextra: datosExtra,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Cliente registrado", color: "success" });
        onGuardado?.(data.cedula || form.cedula);
      } else {
        if (data.tieneventa) setTieneVenta(true);
        showAlert({ message: data.Error || "No se pudo guardar el cliente", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, props = {}) => (
    <TextField
      fullWidth
      label={label}
      value={form[k]}
      onChange={set(k)}
      sx={fpInputSx}
      {...props}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {edicion || existente ? "Editar cliente registrado" : "Nuevo cliente"}
        <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, fontWeight: 500 }}>
          Registro sin factura: la persona aparece en el expediente y la venta se precarga al
          digitar su documento
        </Typography>
      </DialogTitle>
      <DialogContent>
        {cargando ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {tieneVenta && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  onAbrirExpediente ? (
                    <Button size="small" onClick={() => onAbrirExpediente(form.cedula)}>
                      Abrir expediente
                    </Button>
                  ) : null
                }
              >
                Este documento ya tiene facturas o préstamos. Sus datos se editan desde la venta.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DocumentoField
                  value={form.cedula}
                  tipo={tipodoc}
                  onChangeValor={(v) => setForm((p) => ({ ...p, cedula: v }))}
                  onChangeTipo={setTipodoc}
                  onBlur={revisarDocumento}
                  disabled={edicion}
                  autoFocus={!edicion}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {campo("nombres", "Nombres *")}
              </Grid>
              <Grid item xs={12} sm={6}>
                {campo("apellidos", "Apellidos")}
              </Grid>
              <Grid item xs={6} sm={3}>
                {campo("edad", "Edad", {
                  inputProps: { inputMode: "numeric", maxLength: 3 },
                  onChange: (e) =>
                    setForm((p) => ({ ...p, edad: e.target.value.replace(/\D/g, "") })),
                })}
              </Grid>
              <Grid item xs={6} sm={3}>
                {campo("apodo", "Apodo")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {campo("telefono", "Teléfono")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {campo("celular", "Celular")}
              </Grid>
              <Grid item xs={12} sm={4}>
                {campo("email", "Email", { type: "email" })}
              </Grid>
              <Grid item xs={12} sm={8}>
                {campo("direccion", "Dirección")}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={provincias}
                  value={form.provincia || null}
                  onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Provincia" sx={fpInputSx} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                {campo("comentario", "Comentario", { multiline: true, minRows: 2 })}
              </Grid>
            </Grid>
            {secciones.length > 0 && (
              <CamposExtraForm
                secciones={secciones}
                valores={datosExtra}
                onChange={(id, v) => setDatosExtra((p) => ({ ...p, [id]: v }))}
                sectionSx={{ mt: 2.4 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
        <Button
          disableElevation
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={guardando || cargando || tieneVenta}
          sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
